import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteProps, AutocompleteValue, Box, Input, InputAdornment, Stack, Typography, useAutocomplete, UseAutocompleteProps } from "@mui/material";
import { ChangeEvent, KeyboardEvent, FC, useState, useCallback, useEffect, useRef, MutableRefObject } from "react";
import { SimpleOption, useAmbientFacilitiesContext } from "./AmbientFacilitiesContext";
import { useDataCatalogContext } from "../dataCatalogContext/DataCatalogContext";
import { useQueryValueParser } from "./parsers/queryValueParser";
import { Parsed, PathQuery } from "./parsers/types";
import { useCreateSearchInputProxy } from "./useSearchInputProxy";
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'


const emptyParsed: Parsed = { raw: '', ast: {kind: 'Help', value:'?'}, guess:[] }

interface InternalMutableState {
  parsed: Parsed,
  pathProgress: number,
  proxiedInputValue: string,
  popupOpen: boolean,
  keydownHandled: boolean,
  focusInput: Function,
}

const DataCatalogGeneralSearch: FC = () => {
  const [queryValue, setQueryValue] = useState('')
  const [parsedUpdated, setParsedUpdated] = useState(new Date())
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<SuggestionOption[]>([])
  const [declarativeOptionSrc, setDeclarativeOptionSrc] = useState<'Tag'|'Database'|'Schema'|'Table'|'Field'|null>(null)  

  const [errorText, setErrorText] = useState('')
  
  const innerStateRef = useRef<InternalMutableState>({
    // For any input query string 's', the lastest parsed result we can get will be stored
    // at mutable ref 'innerState.parsed'; we don't go with useState because 
    //   ['s' updated; 'parsed' result acquired; immediate 'parsed' result consumption]
    // might all happen within the same tick.
    // But we do need 'parsedUpdate' useState hook to broadcast that 'parsed' has changed
    parsed: emptyParsed,
    pathProgress: -1,
    proxiedInputValue: '',
    popupOpen: false,
    keydownHandled: false,
    focusInput: () => {},
  })
  const innerState = innerStateRef.current
  
  const pushParsedResult = useCallback((x:Parsed) => {
    // console.log(JSON.stringify(x, null, 2))
    setParsedUpdated(new Date())
    innerState.parsed = x
  }, [])
  
  const {
    enqueueParsingRequest,
  } = useQueryValueParser({ pushParsedResult })

  useEffect(() => {
    enqueueParsingRequest(queryValue)
  }, [queryValue])

  const clearQueryValue = () => {
    setQueryValue('')
    innerState.proxiedInputValue = ''
  }

  const { tagOptions, tableOptions, schemaOptions, databaseOptions, fieldOptions, loading: optionsLoading } = useAmbientFacilitiesContext()

  useEffect(() => {
    const pathPartNames = ['Database', 'Schema', 'Table', 'Field']
    if (declarativeOptionSrc === null) { return }
    switch (declarativeOptionSrc) {
      case 'Tag': { setAutoCompleteOptions(tagOptions.map(toTagSuggestion)); break; }
      case 'Database': { setAutoCompleteOptions(databaseOptions.map(toPathSuggestion(0))); break; }
      case 'Schema':   { setAutoCompleteOptions(schemaOptions  .map(toPathSuggestion(1))); break; }
      case 'Table':    { setAutoCompleteOptions(tableOptions   .map(toPathSuggestion(2))); break; }
      case 'Field':    { setAutoCompleteOptions(fieldOptions   .map(toPathSuggestion(3))); break; }
    }
    function toTagSuggestion(x:SimpleOption): TagSuggestionOption {
      return {
        ...x,
        label: x.text,
        tag: x.text,
        id: x.key,
        kind: 'Tag',
        leftAddon: 'Tag'
      }
    }
    function toPathSuggestion(i:number): (x:{text:string}) => PathPartSuggestionOption {
      return (x) => ({
        ...x,
        label: x.text,
        pathPart: x.text,
        kind:'PathPart',
        index: i,
        leftAddon: 'Path',
        comment: x.text === '*' ? `Match All ${pathPartNames[i]}s` : pathPartNames[i],
      })
    }
  }, [declarativeOptionSrc, tagOptions, tableOptions, schemaOptions, databaseOptions, fieldOptions])
  
  const clearOptions = () => {
    setDeclarativeOptionSrc(null)
    setAutoCompleteOptions([])
  }
  
  const displayTagDropdown = () => {
    setDeclarativeOptionSrc('Tag')
  }
  
  const displayPathDropdown = (parsed: Parsed) => {
    if (!parsed.error && parsed.ast.kind === 'Path') {
      const ast = parsed.ast as PathQuery
      const arr = [ast.database, ast.schema, ast.table, ast.field]
      const usages = arr.map(x => x?.usage)
      usages.length = ast.progress
      if (usages.every(x => x === 'exact')) {
        setDeclarativeOptionSrc(['Database', 'Schema', 'Table', 'Field'][ast.progress] as any)
      } else {
        clearOptions()
      }

      if (innerState.pathProgress < ast.progress && ast.amendment) {
        setQueryValue(ast.prefix + ast.amendment)
      }
      innerState.pathProgress = ast.progress
      return;
    }
    innerState.pathProgress = -1
    clearOptions()
  }

  const displayTips = () => {
    setDeclarativeOptionSrc(null)
    const tips: TipsSuggestionOption[] = [
      { kind:'Tips', leftAddon: 'Tips', label: '#', comment: 'try search with "Tags"', action: () => { setQueryValue('#'); setTimeout(() => forcePopupOpen()) } },
      { kind:'Tips', leftAddon: 'Tips', label: '@', comment: 'try search with "Table" name', action: () => { setQueryValue('@') } },
      { 
        kind:'Tips',
        leftAddon: 'Tips',
        label: '/',
        comment: 'try search with "Path" expression',
        action: () => { setQueryValue('/ '); setTimeout(() => forcePopupOpen()) } 
      },      
    ]
    setAutoCompleteOptions(tips)
  }
  
  const displayError = setErrorText
  const clearError = useCallback(() => setErrorText(''), [])

  // react to 'parsed' result change accordingly
  useEffect(() => {
    const parsed = innerState.parsed
    if (parsed.error) {
      displayError(parsed.error)
    } else {
      clearError()
    }

    switch (parsed.ast.kind) {
      case 'Help': {
        displayTips()
        break;
      }
      case 'Tag': {
        displayTagDropdown()
        innerState.proxiedInputValue = parsed.ast.value
        break;
      }
      case 'At': { break; }         // do nothing
      case 'FieldFuzzy': { break; } // do nothing
      case 'Path': {
        displayPathDropdown(parsed)
        break;
      }
    }
    
  }, [parsedUpdated])
  useEffect(() => {
    if (!optionsLoading && !queryValue) {
      setParsedUpdated(new Date())
    }
  }, [optionsLoading, queryValue])
  

  const forcePopupOpen = useCallback(() => {
    innerState.focusInput && innerState.focusInput()
  }, [])

  const { pushCriteria } = useDataCatalogContext()

  const handleQueryKeyup = useCallback((event: KeyboardEvent<HTMLInputElement>): void => {
    const parsed = innerState.parsed
    if ((event.code === 'Enter' || event.code === 'NumpadEnter') && queryValue) {
      if (queryValue === parsed.raw && !innerState.keydownHandled) {
        if (parsed.error) {
          displayError(parsed.error)
          highlightRange(parsed.position)
        } else {
          switch (parsed.ast.kind) {
            case 'Help': {
              displayHelp()
              break;
            }
            case 'Tag': {
              parsed.ast.id && 
                pushCriteria({ kind: 'Tag', tag: parsed.ast.value, id: parsed.ast.id })
              break;
            }
            case 'At': {
              pushCriteria({ kind: 'At', at: parsed.ast.value })
              break;
            }
            case 'FieldFuzzy': {
              pushCriteria({ kind: 'FieldFuzzy', fields: parsed.ast.fields })
              break;
            }
            case 'Path': {
              const ast = parsed.ast
              // after PathPart complete, update input text display
              // first Enter to dismiss autocomplete dropdown, second Enter to confirm input
              const interpreted = ast.prefix + ast.value
              if (parsed.raw !== interpreted) {
                setQueryValue(interpreted)
              } else {
                pushCriteria({ 
                  kind: 'Path', 
                  text: ast.value,
                  database: ast.database && { usage: ast.database.usage, value: ast.database.raw, id: ast.database.id },
                  schema: ast.schema && { usage: ast.schema.usage, value: ast.schema.raw, id: ast.schema.id },
                  table:  ast.table  && { usage: ast.table.usage,  value: ast.table.raw,  id: ast.table.id },
                  field:  ast.field  && { usage: ast.field.usage,  value: ast.field.raw,  id: ast.field.id },
                })
                innerState.parsed = emptyParsed
                clearQueryValue()
              }
              return;
            }
          }
          innerState.parsed = emptyParsed
          clearQueryValue()
        }
      }
    
    } else if (event.code === 'Period' && parsed.ast.kind === 'Path') {
      forcePopupOpen()
    }
    innerState.keydownHandled = false
  }, [queryValue]);
  

  const handleAutocompleteChange: UseAutocompleteProps<SuggestionOption, undefined, true, true>['onChange'] = 
    useCallback((
      event: React.SyntheticEvent,
      option: AutocompleteValue<SuggestionOption, false, true, true>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<SuggestionOption>,
    ) => {
      if (reason === 'selectOption') {
        if (typeof option === 'string') { return }
        switch (option.kind) {
          case 'Tag': {
            pushCriteria({ kind: 'Tag', tag: option.tag, id: option.id })
            clearQueryValue()
            break;
          }
          case 'PathPart': {
            const parsed = innerState.parsed
            if (parsed.error || parsed.ast.kind !== 'Path') { return }
            const ast = parsed.ast
            const arr = [ast.database, ast.schema, ast.table, ast.field]
            arr.length = option.index
            const interpreted = ast.prefix + [...arr.map(x => x?.value), option.pathPart].join('.')
            if (option.index < 3) {
              setQueryValue(interpreted + '.')
              enqueueParsingRequest(interpreted + '.')
              setTimeout(() => {
                setDeclarativeOptionSrc(['Database', 'Schema', 'Table', 'Field'][option.index + 1] as any)
                forcePopupOpen()
              })
            } else {
              setQueryValue(interpreted)
            }
            innerState.proxiedInputValue = ''
            break;
          }
          case 'Tips': {
            option.action()
            break;
          }
        }
        innerState.keydownHandled = true
      }
      clearOptions()
    }, [])

  const { createSearchInputProxy, getOriginalInputValue, getProxyInputValue } = useCreateSearchInputProxy(innerState)
  const renderInput: AutocompleteProps<{ label: string; }, undefined, true, true, "div">['renderInput'] = 
    useCallback((params) => {
      const { InputProps, inputProps, id } = params;
      const { ref: setAnchorRef, endAdornment, ...rest } = InputProps
      const inputRef:MutableRefObject<HTMLInputElement> = (inputProps as any).ref
      const innerOnChange = inputProps.onChange
      innerState.focusInput = inputProps.onFocus as any

      const handleOnChange = (e:ChangeEvent<HTMLInputElement>) => {
        setQueryValue(getOriginalInputValue())
        enqueueParsingRequest(getOriginalInputValue())
        const proxyedVal = getProxyInputValue() || ''
        innerOnChange && innerOnChange({ target:{value:proxyedVal}} as any)
        innerState.proxiedInputValue = proxyedVal
      }
      
      const setProxyInput = (input:HTMLInputElement) => {
        if (input) {
          const proxy = createSearchInputProxy(input)
          inputRef.current = proxy
        } else {
          inputRef.current = input
        }
      }
      return (
        <Input
          id={id}
          ref={setAnchorRef}
          {...rest}
          endAdornment={errorText ? <Typography variant="caption" sx={{color:'warning.main'}}>{errorText}</Typography> : null}
          inputProps={{...inputProps, ref: setProxyInput, onChange: handleOnChange, value:queryValue}}
          disableUnderline={true}
          fullWidth
          onKeyUp={handleQueryKeyup}
          placeholder="Search by field name"
        />
      )
    }, [errorText, queryValue, createSearchInputProxy, getOriginalInputValue])

  return (
    <Box
    >
      <Autocomplete 
        id="data-catalog-general-search"
        options={autoCompleteOptions}
        disableClearable={true}
        forcePopupIcon={true}
        freeSolo={true}
        clearOnBlur={false}
        inputValue={innerState.proxiedInputValue}
        disablePortal
        openOnFocus
        
        renderInput={renderInput}
        renderOption={(props, option, { inputValue }) => {
          const matches = match(option.label, inputValue, {insideWords:true});
          const parts = parse(option.label, matches);

          return (
            <li {...props}>
              <Stack direction="row" justifyContent="center" width="100%">
                <Box sx={{ flex: '0 0 50px', textAlign: 'right' }}>
                  <Typography variant="caption" color="InactiveCaptionText" >{option.leftAddon}</Typography>
                </Box>
                <Box sx={{ pl: 1, pr: 2 }}>
                  <Typography variant="caption" color="InactiveCaptionText" >|</Typography>
                </Box>
                <Box sx={{ flex: '1 1 0', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </Box>
                <Box sx={{ maxWidth: 250, ml: 1, mr: 2 }}>
                  {!!option.comment && <Typography variant="caption" color="InactiveCaptionText">{option.comment}</Typography>}
                </Box>
              </Stack>
            </li>
          );
        }}

        onChange={handleAutocompleteChange}
        onOpen={() => innerState.popupOpen = true}
        onClose={() => innerState.popupOpen = false}

      />
    </Box>
  )
}

export default DataCatalogGeneralSearch;

type Command =
  | 'UpdateQuery'
  | 'AskSuggestion'
  | 'RenderSuggestion'
  | 'AcceptSuggestion'
  | 'AcceptQuery'


function highlightRange(position: [number, number] | undefined) {
  // throw new Error("Function not implemented.");
  console.log('Function "highlightRange" not implemented.')
}

function displayHelp() {
  // throw new Error("Function not implemented.");
  console.log('Function "displayHelp" not implemented.')
}


// 
// * input text display
// 
// AcceptSuggestion: pick a suggestion from autocomplete dropdown
//   for query types other than path query, this also mean AcceptQuery;
//   for path query, this means a suggestion of a path section is chosen, we shall continue
//   prompt suggestions about next section to the user; until the last piece (the field section)
//   is picked, can we view it as AcceptQuery
// 
// AcceptQuery: hit enter key in the input box, user is done filling this query criteria
//   for path query, hit enter key would mean to accept current input as a fuzzy search for
//   a path section; until the field section is picked, can we view it as a criteria completed.
//   
// For path query, whenever a path section is picked (be it fuzzy or exact), input text display
// shall be updated to reflect its correct meaning
// 
// When AcceptQuery is fired, the validation status of parsed result shall be checked, if 
// validation failed, we shall prevent user from accept the query, and showing the error message,
// and auto select the text range causing the error
// 

interface Suggestion {
  label: string,
  kind: 'Tag' | 'PathPart' | 'Tips',
  leftAddon: string,
  comment?: string,
}

type SuggestionOption = | TagSuggestionOption | PathPartSuggestionOption | TipsSuggestionOption

interface TagSuggestionOption extends Suggestion {
  kind: 'Tag',
  tag: string,
  id: number,
}

interface PathPartSuggestionOption extends Suggestion {
  kind: 'PathPart',
  pathPart: string,
  index: number,
}

interface TipsSuggestionOption extends Suggestion {
  kind: 'Tips',
  action: Function,
}
